import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/auth";
import { getAllCategoriesWithId } from "@/services/categoryService";
import { getAllAreas } from "@/services/areaService";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminEditListingHeader from "@/components/admin/edit-listing/AdminEditListingHeader";
import AdminEditListingPreview from "@/components/admin/edit-listing/AdminEditListingPreview";
import AdminCreateListingForm from "@/components/admin/edit-listing/AdminCreateListingForm";
import { useToast } from "@/hooks/use-toast";
import { useCreateAdminListing } from "@/hooks/admin/useAdminListingActions";

interface FormData {
  name: string;
  categories: string[];
  description: string;
  websiteUrl: string;
  suburbs: string[];
  imageUrl: string;
  isFeatured: boolean;
  isVerified: boolean;
  // Admin-only fields for unassigned listings
  whatsappNumber: string;
}

const AdminCreateListingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const createListing = useCreateAdminListing();

  const [formData, setFormData] = useState<FormData>({
    name: "",
    categories: [],
    description: "",
    websiteUrl: "",
    suburbs: [],
    imageUrl: "",
    isFeatured: false,
    isVerified: false,
    whatsappNumber: "",
  });
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const {
    data: categories = [],
  } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      return await getAllCategoriesWithId();
    },
  });

  const {
    data: areas = [],
  } = useQuery({
    queryKey: ["areas"],
    queryFn: async () => {
      return await getAllAreas();
    },
  });

  const areasGrouped = areas.reduce((acc, area) => {
    if (!acc[area.territory]) {
      acc[area.territory] = [];
    }
    acc[area.territory].push(area.name);
    return acc;
  }, {} as Record<string, string[]>);

  const handleImageChange = (file: File) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleFieldChange = (field: string, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleToggle = (field: string) => {
    setFormData(prev => ({ ...prev, [field]: !prev[field as keyof FormData] }));
  };

  const handleCategorySelect = (categoryName: string) => {
    // Apply free-tier limits for unassigned listings
    if (formData.categories.length >= 1 && !formData.categories.includes(categoryName)) {
      toast({
        title: "Category Limit Reached",
        description: "Unassigned listings are limited to 1 category (free tier rules).",
        variant: "destructive",
      });
      return;
    }
    
    if (!formData.categories.includes(categoryName)) {
      setFormData(prev => ({
        ...prev,
        categories: [...prev.categories, categoryName]
      }));
    }
  };

  const handleCategoryRemove = (category: string) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.filter(c => c !== category)
    }));
  };

  const handleSuburbAdd = (suburb: string) => {
    // Apply free-tier limits for unassigned listings
    if (formData.suburbs.length >= 2 && !formData.suburbs.includes(suburb)) {
      toast({
        title: "Area Limit Reached",
        description: "Unassigned listings are limited to 2 areas (free tier rules).",
        variant: "destructive",
      });
      return;
    }
    
    if (!formData.suburbs.includes(suburb)) {
      setFormData(prev => ({
        ...prev,
        suburbs: [...prev.suburbs, suburb]
      }));
    }
  };

  const handleSuburbRemove = (suburb: string) => {
    setFormData(prev => ({
      ...prev,
      suburbs: prev.suburbs.filter(s => s !== suburb)
    }));
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    
    // Validation
    if (!formData.name.trim()) {
      toast({
        title: "Validation Error",
        description: "Service name is required.",
        variant: "destructive",
      });
      return;
    }
    
    if (formData.categories.length === 0) {
      toast({
        title: "Validation Error",
        description: "At least one category is required.",
        variant: "destructive",
      });
      return;
    }
    
    if (!formData.description.trim()) {
      toast({
        title: "Validation Error",
        description: "Description is required.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      const newListing = await createListing.mutateAsync({
        name: formData.name,
        category: formData.categories[0] || "",
        categories: formData.categories,
        description: formData.description,
        websiteUrl: formData.websiteUrl || null,
        suburbs: formData.suburbs,
        imageUrl: imagePreview || formData.imageUrl || "/default-banner.png",
        isFeatured: formData.isFeatured,
        isVerified: formData.isVerified,
        ownerUserId: null,
        assignmentMode: 'unassigned',
        initialStatus: 'approved',
        whatsappNumber: formData.whatsappNumber || null,
      });

      toast({
        title: "Listing Created",
        description: "The service listing has been successfully created.",
      });

      navigate(`/admin/edit-listing/${newListing.listingId}`);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to create the service listing.';
      toast({
        title: "Creation Failed",
        description: message,
        variant: "destructive",
      });
    }
  };

  return (
    <AdminLayout title="Create New Listing">
      <AdminEditListingHeader
        listing={{ name: formData.name || "New Listing", status: "approved" }}
        isCreating={true}
      />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <AdminCreateListingForm
            formData={formData}
            categories={categories}
            areasGrouped={areasGrouped}
            isDisabled={createListing.isPending}
            onSubmit={handleSubmit}
            onFieldChange={handleFieldChange}
            onToggle={handleToggle}
            onCategorySelect={handleCategorySelect}
            onCategoryRemove={handleCategoryRemove}
            onSuburbAdd={handleSuburbAdd}
            onSuburbRemove={handleSuburbRemove}
          />
        </div>
        <div>
          <AdminEditListingPreview
            formData={formData}
            imagePreview={imagePreview}
            onImageChange={handleImageChange}
          />
        </div>
      </div>
      <div className="flex justify-end gap-3 mt-6">
        <button
          type="button"
          onClick={() => navigate("/admin/review-listings")}
          className="px-4 py-2 text-gray-600 hover:text-gray-800"
          disabled={createListing.isPending}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          className="bg-whatsapp hover:bg-whatsapp-dark text-white px-6 py-2 rounded-lg flex items-center disabled:opacity-50"
          disabled={createListing.isPending}
        >
          {createListing.isPending ? "Creating..." : "Create Listing"}
        </button>
      </div>
    </AdminLayout>
  );
};

export default AdminCreateListingPage;
